import { Component, OnInit, OnDestroy, EventEmitter, Output } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { ReplaySubject } from 'rxjs';
import { takeUntil, concatMap } from 'rxjs/operators';
import { CameraService, CameraServiceBase } from '../../service/camera.service';
import { PortraitService, PortraitServiceBase } from '../../service/portrait.service';
import { CameraServiceState, SizeInfo, CameraInfo, CameraType } from '../../service/hw-service-model';
import { CameraSettings, ComponentType } from '../cam.auto.connect';
import { Control } from '../../base/control/control';
import { AutoAction, BatteryAutoActionHelper } from '../auto.action';
import { SystemService, SystemServiceFactory } from '../../service/system.service';
import { HWServiceConfig } from '../../service/hw-service-config';

@Component({
  selector: 'i-tablet-sign-photo-wizard',
  templateUrl: './tablet-sign-photo-wizard.component.html',
  styleUrls: ['./tablet-sign-photo-wizard.component.scss'],
  providers: [
    { provide: CameraServiceBase, useClass: CameraService },
    { provide: PortraitServiceBase, useClass: PortraitService },
  ],
})
export class TabletSignPhotoWizardComponent implements OnInit, OnDestroy {
  private imageSrc: SafeUrl | null = null;
  private frameSize: SizeInfo | null = null;
  private chipPhoto: string | null = null;
  camList: CameraInfo[];
  rotateToogle: boolean = true;
  ListSize: SizeInfo[];
  TIME: string;

  public ligthsOn: boolean = false;
  private frameResolution = 800 * 600;
  private dead: ReplaySubject<void> = new ReplaySubject();
  private autoCancel = new AutoAction(
    (text) => (this.TIME = text),
    () => this.cancel(),
  );
  private systemService: SystemService;
  private batteryHelper: BatteryAutoActionHelper;
  tabletSignWizard: TabletSignWizard = TabletSignWizard.Closed;

  @Output() onComponentClosureRequested = new EventEmitter<string | null>();

  public constructor(
    private cameraService: CameraServiceBase,
    private portraitService: PortraitServiceBase,
    private settingsServiceFactory: SystemServiceFactory,
    private config: HWServiceConfig,
  ) {
    this.systemService = this.settingsServiceFactory.create(this.config);
    this.batteryHelper = new BatteryAutoActionHelper(this.autoCancel, this.systemService, this.dead);
  }

  public ngOnInit(): void {
    this.portraitService.connect();
    this.cameraConnection();
    this.batteryHelper.run();
  }

  public ngOnDestroy(): void {
    this.shutdownServices();
    this.dead.next();
    this.dead.complete();
  }

  public get state$(): string {
    return TabletSignWizard[this.tabletSignWizard];
  }

  public get isInitializationScreenVisible$(): boolean {
    return this.tabletSignWizard === TabletSignWizard.InitializationInProgress;
  }

  public get isCropScreenVisible$(): boolean {
    return this.tabletSignWizard === TabletSignWizard.PhotoProcessingInProgress;
  }

  public get localPreview$(): boolean {
    return this.tabletSignWizard === TabletSignWizard.Preview;
  }

  public get isInitializationErrorScreenVisible$(): boolean {
    return this.tabletSignWizard === TabletSignWizard.InitializationError;
  }

  public get rotateCameraScreen$(): boolean {
    return this.tabletSignWizard === TabletSignWizard.RotateCameraScreen;
  }

  public get shootingInProgress$(): boolean {
    return this.tabletSignWizard === TabletSignWizard.ShootingInProgress;
  }

  public get isShootingScreenVisible$(): boolean {
    return (
      this.tabletSignWizard !== TabletSignWizard.InitializationInProgress &&
      this.tabletSignWizard !== TabletSignWizard.InitializationError &&
      this.tabletSignWizard !== TabletSignWizard.ShootingResultConfirmation &&
      this.tabletSignWizard !== TabletSignWizard.ShutdownInProgress &&
      this.tabletSignWizard !== TabletSignWizard.Closed
    );
  }

  public get isConfirmationScreenVisible$(): boolean {
    return this.tabletSignWizard === TabletSignWizard.ShootingResultConfirmation;
  }

  public get imageSource$(): SafeUrl | null {
    return this.imageSrc;
  }

  private cameraConnection() {
    this.cameraService.cameraState$.pipe(takeUntil(this.dead)).subscribe(
      (state) => this.onCameraStateChanged(state),
      (error) => this.handleError(error, TabletSignWizard.InitializationError),
    );
    this.tabletSignWizard = TabletSignWizard.InitializationInProgress;
    setTimeout(() => {
      this.cameraService.connect();
    }, 1);
  }

  public enroll(): void {
    this.tabletSignWizard = TabletSignWizard.ShootingInProgress;
    this.portraitService
      .getID()
      .pipe(
        concatMap((id) => this.cameraService.enroll(id, false, 'raw')),
        concatMap((x) => this.portraitService.binarizeSignature()),
        concatMap((y) => this.portraitService.download(false)),
        takeUntil(this.dead),
      )
      .subscribe(
        (r) => {
          Control.readBlob(r, (data) => {
            this.onPhotoProcessingComplete(data);
          });
        },
        (error) => {
          this.setLightMode(false);
          this.handleError(error);
        },
        () => {
          this.setLightMode(false);
        },
      );
  }

  public cancel(): void {
    this.onComponentClosureRequested.emit(null);
  }

  public canChangeLightMode(): boolean {
    let cameraId = this.cameraService.cameraId$;
    let selectedCamera: CameraInfo = this.camList.find((ci) => ci.id === cameraId)!;
    return this.tabletSignWizard === TabletSignWizard.Preview && selectedCamera.type === CameraType.Back;
  }

  public changeLightMode() {
    let enable = !this.ligthsOn;
    this.setLightMode(enable);
    CameraSettings.setLightMode(ComponentType.SignatureWizard, enable);
  }

  private setLightMode(enable: boolean) {
    this.ligthsOn = enable;
    this.cameraService.lights(enable);
  }

  private onCameraStateChanged(state: CameraServiceState): void {
    if (state !== CameraServiceState.Closed && this.tabletSignWizard === TabletSignWizard.ShutdownInProgress) {
      this.shutdownServices();
      return;
    }
    this.onProcessorStateShanged(state);
  }

  private onProcessorStateShanged(state: CameraServiceState): void {
    if (this.tabletSignWizard === TabletSignWizard.InitializationInProgress) {
      this.cameraService
        .getCameraList()
        .pipe(takeUntil(this.dead))
        .subscribe(
          (list) => this.onCameraListReceived(list),
          (error) => this.handleError(error, TabletSignWizard.InitializationError),
        );
    }
    if (this.tabletSignWizard === TabletSignWizard.ShutdownInProgress) {
      this.shutdownServices();
      return;
    }
  }

  private onCameraListReceived(list: CameraInfo[]): void {
    this.camList = list;
    let selectedCamera = CameraSettings.selectCamera(ComponentType.SignatureWizard, list, CameraType.Back);
    if (selectedCamera) {
      this.startPreview(selectedCamera);
    } else {
      this.handleError('Невозможно подключиться к камере', TabletSignWizard.InitializationError);
    }
  }

  rotate() {
    this.tabletSignWizard = TabletSignWizard.RotateCameraScreen;
    this.cameraService
      .stopPreview()
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => {
          let anotherCamera = CameraSettings.selectAnotherCamera(ComponentType.SignatureWizard, this.camList, this.cameraService.cameraId$);
          if (anotherCamera) {
            this.rotateToogle = !this.rotateToogle;
            this.startPreview(anotherCamera);
          } else {
            this.handleError('Невозможно подключиться к камере', TabletSignWizard.InitializationError);
          }
        },
        (error) => this.handleError(error, TabletSignWizard.InitializationError),
      );
  }

  private startPreview(selectedCamera: CameraInfo) {
    this.cameraService.cameraId$ = selectedCamera.id;
    this.frameSize = CameraSettings.selectFrameByFormatThenArea(selectedCamera.frames, 'YUYV', this.frameResolution);
    this.cameraService.startLocalPreviewForSignature(this.frameSize).subscribe(
      (r) => {
        this.tabletSignWizard = TabletSignWizard.Preview;
      },
      (error) => this.handleError(error, TabletSignWizard.InitializationError),
    );
    this.setLightMode(CameraSettings.getLightMode(ComponentType.SignatureWizard, selectedCamera));
  }

  replayShoot() {
    this.tabletSignWizard = TabletSignWizard.Preview;
    this.portraitService
      .download(false)
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => {
          this.startPreview(this.getCamera());
        },
        (e) => {
          this.startPreview(this.getCamera());
        },
      );
  }

  private getCamera(): CameraInfo {
    return this.camList.find((camera) => camera.id === this.cameraService.cameraId$)!;
  }

  private onPhotoProcessingComplete(result: string) {
    this.chipPhoto = 'data:image/png;base64,' + result;
    this.imageSrc = 'data:image/png;base64,' + result;
    this.tabletSignWizard = TabletSignWizard.ShootingResultConfirmation;
  }

  confirmPhoto() {
    this.onComponentClosureRequested.emit(this.chipPhoto);
  }

  private handleError(error: any, state: TabletSignWizard | null = null): void {
    console.error(error);
    if (state) {
      this.tabletSignWizard = state;
    }
  }

  private shutdownServices(): void {
    this.cameraService.lights(false);
    this.cameraService.disconnect();
    this.portraitService.disconnect();
    this.systemService.disconnect().subscribe();
    this.dead.next();
    this.dead.complete();
    this.tabletSignWizard = TabletSignWizard.Closed;
  }
}

export enum TabletSignWizard {
  InitializationInProgress,
  InitializationError,
  Preview,
  RotateCameraScreen,
  ShootingInProgress,
  PhotoProcessingInProgress,
  ShootingResultConfirmation,
  ShutdownInProgress,
  Closed,
}
